import { useCallback, useEffect, useRef } from "react";
import isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";

import DrawerSnap from "@components/shared/DrawerSnap/DrawerSnap";
import { DRAWER_HEIGHT } from "@constants/Drawer";
import { useDrawerLimits } from "@hooks/useDrawerLimits/useDrawerLimits";
import { BookingFetchResponseType } from "@models/services/booking";
import { locationType } from "@models/services/organisation";
import bookingCalendarSlice from "@slices/bookingCalendar.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";

import DrawerTimeSlot from "../DrawerTimeSlot/DrawerTimeSlot";

const BookingCalendarDrawer = ({
  bookingSlots,
  checkBookingDisable,
  locationContext,
  priceLoader,
}: {
  checkBookingDisable: () => boolean;
  bookingSlots: BookingFetchResponseType;
  locationContext?: locationType;
  priceLoader: boolean;
}) => {
  const dispatch = useDispatch();

  const { maxHeight } = useDrawerLimits();

  const showBookingDrawer = useSelector(
    (state: RootState) => state.drawer.showBookingDrawer
  );

  const openPercent = useSelector(
    (state: RootState) => state.drawer.openPercent
  );

  const prevMaxHeightRef = useRef<number | null>(null);

  const handleDrawerClose = () => {
    dispatch(drawerSlice.actions.removeDrawerBookingState(null));
    dispatch(bookingCalendarSlice.actions.setTempEvents([]));
  };

  const updateDrawerHeight = useCallback(() => {
    if (!showBookingDrawer) return;

    if (openPercent <= DRAWER_HEIGHT.SECONDARY_POSITION) {
      return;
    }

    if (
      prevMaxHeightRef.current === null ||
      prevMaxHeightRef.current !== maxHeight
    ) {
      prevMaxHeightRef.current = maxHeight;
      dispatch(drawerSlice.actions.setOpenPercent(maxHeight));
    }
  }, [maxHeight, openPercent, showBookingDrawer, dispatch]);

  useEffect(() => {
    updateDrawerHeight();
  }, [updateDrawerHeight]);

  return (
    <DrawerSnap
      isOpen={showBookingDrawer}
      onClose={handleDrawerClose}
      direction="bottom"
      useSnapPoints={true}
    >
      {isEmpty(bookingSlots) ? null : (
        <DrawerTimeSlot
          isBookingDisable={checkBookingDisable()}
          locationContext={locationContext}
          priceLoader={priceLoader}
          bookingSlots={bookingSlots}
        />
      )}
    </DrawerSnap>
  );
};

export default BookingCalendarDrawer;
